import { useAuth } from "@clerk/clerk-react";
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { blue, green, grey, orange } from "@mui/material/colors";
import { useState } from "react";
import { BentoBrand, BentoBrandPartnership } from "schemas/dashboard";
import { SetAlertType } from "schemas/functions";

import { fetcherAuth } from "utils/api";
import { formatNumber, generateInitials } from "utils/string";

interface BrandContact {
  id: number;
  name: string;
  title: string;
  email: string;
  source: string;
  best_response_ranking: number | null;
  best_response_type: string | null;
  last_sent_to_at: number | null;
}

interface RecentResponse {
  best_response_ranking: number;
  best_response_type: string;
  timestamp: number;
}

interface SearchBrand extends BentoBrand {
  contacts?: BrandContact[];
  contactedAmount?: number;
  nicheness?: number;
  isLocalOnly?: boolean;
  recentResponses?: RecentResponse[];
  // snake_to_camel converts "_score" to "Score"
  Score?: number;
}

interface Niche {
  id: number;
  name: string;
  status: string;
  locationName: string | null;
}

type SearchType = "automations" | "repitching" | "saved_brands";

interface BrandSearchDialogProps {
  open: boolean;
  onClose: () => void;
  organizationId: number;
  niches: Niche[];
  setAlert: SetAlertType;
}

export default function BrandSearchDialog({
  open,
  onClose,
  organizationId,
  niches,
  setAlert,
}: BrandSearchDialogProps) {
  const { getToken } = useAuth();

  const [searchType, setSearchType] = useState<SearchType>("automations");
  const [counterMetricsDisabled, setCounterMetricsDisabled] = useState(false);
  const [alreadyEmailedDisabled, setAlreadyEmailedDisabled] = useState(false);
  const [selectedNicheIds, setSelectedNicheIds] = useState<number[]>(
    niches.map((n) => n.id),
  );
  const [limit, setLimit] = useState(100);

  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<SearchBrand[]>([]);
  const [totalResults, setTotalResults] = useState(0);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [queryObject, setQueryObject] = useState<any>(null);

  const handleSearch = async () => {
    if (selectedNicheIds.length === 0) {
      setAlert("Please select at least one niche", "warning");
      return;
    }
    setLoading(true);
    setResults([]);
    setTotalResults(0);
    setQueryObject(null);
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/admin/virtual-assistant-users/${organizationId}/search-brands`,
        "POST",
        {},
        {
          organizationNicheIds: selectedNicheIds,
          counterMetricsDisabled,
          alreadyEmailedBrandsDisabled: alreadyEmailedDisabled,
          searchType,
          limit,
        },
      );
      setResults(res.bentoBrands || []);
      setTotalResults(res.totalResults || 0);
      setQueryObject(res.query || null);
    } catch (error: any) {
      setAlert(error?.message || "Search failed", "warning");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleNiche = (nicheId: number) => {
    setSelectedNicheIds((prev) =>
      prev.includes(nicheId)
        ? prev.filter((id) => id !== nicheId)
        : [...prev, nicheId],
    );
  };

  const handleSelectAllNiches = () => {
    if (selectedNicheIds.length === niches.length) {
      setSelectedNicheIds([]);
    } else {
      setSelectedNicheIds(niches.map((n) => n.id));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <DialogTitle>
        <Grid container alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Brand Search</Typography>
          <IconButton onClick={onClose}>
            <Typography variant="body2">X</Typography>
          </IconButton>
        </Grid>
      </DialogTitle>

      <DialogContent>
        {/* Controls */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "flex-end",
            gap: 3,
            mb: 3,
            p: 2,
            bgcolor: grey[50],
            borderRadius: 2,
          }}
        >
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 0.5, display: "block" }}
            >
              Search Type
            </Typography>
            <ToggleButtonGroup
              value={searchType}
              exclusive
              onChange={(_, val) => val && setSearchType(val)}
              size="small"
            >
              <ToggleButton value="automations">Automations</ToggleButton>
              <ToggleButton value="repitching">Repitching</ToggleButton>
              <ToggleButton value="saved_brands">Saved Brands</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 0.5, display: "block" }}
            >
              Limit
            </Typography>
            <Select
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              size="small"
              sx={{ minWidth: 80 }}
            >
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
              <MenuItem value={200}>200</MenuItem>
            </Select>
          </Box>

          <Box sx={{ display: "flex", gap: 1 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={counterMetricsDisabled}
                  onChange={(e) => setCounterMetricsDisabled(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Typography variant="body2">Disable Counter Metrics</Typography>
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={alreadyEmailedDisabled}
                  onChange={(e) => setAlreadyEmailedDisabled(e.target.checked)}
                  size="small"
                />
              }
              label={
                <Typography variant="body2">
                  Disable Already Emailed Filter
                </Typography>
              }
            />
          </Box>
        </Box>

        {/* Niche Selection */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Niches
            </Typography>
            <Button size="small" onClick={handleSelectAllNiches}>
              {selectedNicheIds.length === niches.length
                ? "Deselect All"
                : "Select All"}
            </Button>
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {niches.map((niche) => (
              <Chip
                key={niche.id}
                label={`${niche.name}${
                  niche.locationName ? ` (${niche.locationName})` : ""
                }`}
                size="small"
                color={
                  selectedNicheIds.includes(niche.id) ? "primary" : "default"
                }
                variant={
                  selectedNicheIds.includes(niche.id) ? "filled" : "outlined"
                }
                onClick={() => handleToggleNiche(niche.id)}
                sx={{ cursor: "pointer" }}
              />
            ))}
            {niches.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No niches available.
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={loading || selectedNicheIds.length === 0}
          >
            {loading ? "Searching..." : "Search Brands"}
          </Button>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Results */}
        {loading ? (
          <Grid container justifyContent="center" sx={{ py: 4 }}>
            <CircularProgress />
          </Grid>
        ) : results.length > 0 ? (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 2,
              }}
            >
              <Typography variant="subtitle2" color="text.secondary">
                {totalResults} results
              </Typography>
              {queryObject && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    navigator.clipboard.writeText(
                      JSON.stringify(queryObject, null, 2),
                    );
                    setAlert("Query copied to clipboard", "success");
                  }}
                >
                  Copy Query
                </Button>
              )}
            </Box>
            <Grid container spacing={2}>
              {results.map((brand) => (
                <Grid item xs={12} md={6} key={brand.id}>
                  <BrandResultCard brand={brand} />
                </Grid>
              ))}
            </Grid>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Stat pill
// ---------------------------------------------------------------------------

function StatValue({
  label,
  value,
  color,
}: {
  label: string;
  value: React.ReactNode;
  color?: string;
}) {
  return (
    <Box sx={{ textAlign: "center", minWidth: 48 }}>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          lineHeight: 1.1,
          color: color || "text.primary",
        }}
      >
        {value}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontSize: 10, lineHeight: 1.2 }}
      >
        {label}
      </Typography>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Brand Card
// ---------------------------------------------------------------------------

function BrandResultCard({ brand }: { brand: SearchBrand }) {
  const contacts = brand.contacts || [];
  const partnerships = brand.partnerships || [];
  const recentResponses = brand.recentResponses || [];

  const contactedTotal = brand.contactedAmount ?? 0;
  const contactedRecently = brand.contactedRecentlyAmount ?? 0;
  const nicheness = brand.nicheness;
  const esScore = brand.Score;

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: grey[200],
        borderRadius: 2,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        transition: "box-shadow 0.15s",
        "&:hover": { boxShadow: 2 },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          p: 2,
          pb: 1,
        }}
      >
        {brand.logoUrl ? (
          <Box
            component="img"
            src={brand.logoUrl}
            sx={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              border: "1px solid",
              borderColor: grey[200],
              objectFit: "contain",
              flexShrink: 0,
            }}
          />
        ) : (
          <Avatar
            sx={{
              bgcolor: green[700],
              width: 36,
              height: 36,
              fontSize: 14,
              flexShrink: 0,
            }}
          >
            {generateInitials(brand.brandName)}
          </Avatar>
        )}

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, lineHeight: 1.3 }}
            noWrap
          >
            {brand.brandName}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {brand.instagramFollowerCount != null && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: 11 }}
              >
                {formatNumber(brand.instagramFollowerCount)}
              </Typography>
            )}
            {brand.instagramUsername && (
              <Link
                href={`https://instagram.com/${brand.instagramUsername}`}
                target="_blank"
                sx={{ fontSize: 11 }}
                onClick={(e) => e.stopPropagation()}
              >
                @{brand.instagramUsername}
              </Link>
            )}
            {brand.website && (
              <>
                <Typography variant="caption" color="text.secondary">
                  |
                </Typography>
                <Link
                  href={`https://${brand.website}`}
                  target="_blank"
                  sx={{ fontSize: 11 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {brand.website.split("/")[0]}
                </Link>
              </>
            )}
          </Box>
        </Box>
      </Box>

      {/* Stat Row */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-around",
          px: 2,
          py: 1,
          borderTop: "1px solid",
          borderBottom: "1px solid",
          borderColor: grey[100],
          bgcolor: grey[50],
        }}
      >
        <StatValue
          label="ES Score"
          value={esScore != null ? Math.round(esScore) : "-"}
          color={blue[700]}
        />
        <StatValue
          label="Nicheness"
          value={nicheness != null ? nicheness.toFixed(1) : "-"}
          color={
            nicheness != null
              ? nicheness >= 7
                ? green[700]
                : nicheness >= 4
                  ? orange[800]
                  : grey[600]
              : undefined
          }
        />
        <StatValue
          label="Contacted"
          value={contactedTotal}
          color={contactedTotal > 6 ? orange[800] : undefined}
        />
        <StatValue
          label="Recent"
          value={contactedRecently}
          color={contactedRecently > 3 ? orange[800] : undefined}
        />
        <StatValue label="Contacts" value={contacts.length} />
        <StatValue label="Partnerships" value={partnerships.length} />
      </Box>

      {/* Badges / Tags */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 0.5,
          px: 2,
          py: 1,
        }}
      >
        {brand.emailedBrand && (
          <Chip
            label="Pitched"
            size="small"
            color="info"
            variant="outlined"
            sx={{ fontSize: 11, height: 22 }}
          />
        )}
        {brand.compensationTypes?.map((ct) => (
          <Chip
            key={ct}
            label={ct.replace(/_/g, " ")}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ fontSize: 11, height: 22, textTransform: "capitalize" }}
          />
        ))}
        {brand.metadata?.map((tag, i) => (
          <Chip
            key={`m-${i}`}
            label={tag.value}
            size="small"
            sx={{
              fontSize: 11,
              height: 22,
              bgcolor: "white",
              border: `0.5px solid ${
                tag.type === "location" ? grey[900] : grey[500]
              }`,
              fontWeight: tag.type === "location" ? 600 : 400,
            }}
          />
        ))}
      </Box>

      {/* Location */}
      <LocationSection brand={brand} />

      {/* About */}
      {brand.aboutSummary && (
        <Box sx={{ px: 2, pb: 1 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: 12 }}
          >
            {brand.aboutSummary}
          </Typography>
        </Box>
      )}

      {/* Recent Responses */}
      {recentResponses.length > 0 && (
        <Box
          sx={{ px: 2, py: 1, borderTop: "1px solid", borderColor: grey[100] }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 600, display: "block", mb: 0.5 }}
          >
            Recent Responses ({recentResponses.length})
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {recentResponses.slice(0, 8).map((r, i) => (
              <Chip
                key={i}
                label={`${r.best_response_type} (${new Date(
                  r.timestamp * 1000,
                ).toLocaleDateString()})`}
                size="small"
                color={getResponseColor(r.best_response_type)}
                variant="outlined"
                sx={{ fontSize: 10, height: 20 }}
              />
            ))}
            {recentResponses.length > 8 && (
              <Typography variant="caption" color="text.disabled">
                +{recentResponses.length - 8} more
              </Typography>
            )}
          </Box>
        </Box>
      )}

      <Divider />

      {/* Contacts Table */}
      <Box sx={{ px: 2, py: 1.5 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontWeight: 600, display: "block", mb: 0.5 }}
        >
          Contacts ({contacts.length})
        </Typography>
        {contacts.length > 0 ? (
          <TableContainer
            sx={{
              border: "1px solid",
              borderColor: grey[200],
              borderRadius: 1,
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: grey[50] }}>
                  <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>
                    Name
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>
                    Email
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>
                    Title
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>
                    Source
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>
                    Best Response
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>
                    Last Sent
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {contacts.map((contact) => (
                  <TableRow key={contact.id}>
                    <TableCell sx={{ fontSize: 12 }}>
                      {contact.name || "-"}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>
                      {contact.email || "-"}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>
                      {contact.title || "-"}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>
                      {contact.source || "-"}
                    </TableCell>
                    <TableCell>
                      {contact.best_response_type ? (
                        <Chip
                          label={contact.best_response_type}
                          size="small"
                          color={getResponseColor(contact.best_response_type)}
                          variant="outlined"
                          sx={{ fontSize: 10, height: 20 }}
                        />
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>
                      {contact.last_sent_to_at
                        ? new Date(
                            contact.last_sent_to_at * 1000,
                          ).toLocaleDateString()
                        : "-"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: 12 }}
          >
            No contacts.
          </Typography>
        )}
      </Box>

      {/* Partnerships */}
      <Box sx={{ px: 2, pb: 2 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontWeight: 600, display: "block", mb: 0.5 }}
        >
          Partnerships ({partnerships.length})
        </Typography>
        {partnerships.length > 0 ? (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {partnerships.map((p, i) => (
              <PartnershipChip key={i} partnership={p} />
            ))}
          </Box>
        ) : (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: 12 }}
          >
            No partnerships.
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Location section
// ---------------------------------------------------------------------------

function LocationSection({ brand }: { brand: SearchBrand }) {
  const locations = brand.locationNames || [];
  const countries = brand.countries || [];
  const continents = brand.continents || [];
  const hasLocation =
    locations.length > 0 || countries.length > 0 || continents.length > 0;
  const hasScope = brand.isInternational || brand.isLocalOnly;

  if (!hasLocation && !hasScope) return null;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 2,
        py: 0.75,
        borderTop: "1px solid",
        borderColor: grey[100],
        bgcolor: grey[50],
        fontSize: 12,
      }}
    >
      <Box
        component="i"
        className="fa-thin fa-location-dot"
        sx={{ color: grey[600], fontSize: 13 }}
      />

      {brand.isInternational && (
        <Chip
          label="International"
          size="small"
          sx={{
            fontSize: 11,
            height: 20,
            bgcolor: blue[50],
            color: blue[800],
            borderColor: blue[200],
          }}
          variant="outlined"
        />
      )}
      {brand.isLocalOnly && (
        <Chip
          label="Local Only"
          size="small"
          sx={{
            fontSize: 11,
            height: 20,
            bgcolor: orange[50],
            color: orange[900],
            borderColor: orange[200],
          }}
          variant="outlined"
        />
      )}

      {locations.length > 0 && (
        <Typography variant="caption" color="text.secondary">
          {locations.join(", ")}
        </Typography>
      )}

      {locations.length === 0 && countries.length > 0 && (
        <Typography variant="caption" color="text.secondary">
          {countries.join(", ")}
        </Typography>
      )}

      {continents.length > 0 && (
        <Typography variant="caption" color="text.disabled" sx={{ ml: 0.5 }}>
          ({continents.join(", ")})
        </Typography>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Partnership chip
// ---------------------------------------------------------------------------

function PartnershipChip({
  partnership,
}: {
  partnership: BentoBrandPartnership;
}) {
  const compTypes = Array.isArray(partnership.compensation_types)
    ? partnership.compensation_types.join(", ")
    : partnership.compensation_types || "";
  const country = partnership.country || partnership.organization_country_code;
  const status = partnership.status;

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: grey[200],
        borderRadius: 1,
        p: 1,
        fontSize: 12,
        display: "flex",
        flexDirection: "column",
        gap: 0.25,
        bgcolor: grey[50],
      }}
    >
      <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
        <Chip
          label={status}
          size="small"
          color={
            status === "active"
              ? "success"
              : status === "completed"
                ? "info"
                : "default"
          }
          variant="outlined"
          sx={{ fontSize: 10, height: 20 }}
        />
        {country && (
          <Typography variant="caption" color="text.secondary">
            {country}
          </Typography>
        )}
      </Box>
      {compTypes && (
        <Typography variant="caption" color="text.secondary">
          {compTypes}
        </Typography>
      )}
      {partnership.created_at && (
        <Typography variant="caption" color="text.secondary">
          {new Date(partnership.created_at * 1000).toLocaleDateString()}
        </Typography>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getResponseColor(
  responseType: string,
): "success" | "warning" | "error" | "default" {
  const lower = responseType.toLowerCase();
  if (lower === "positive" || lower === "interested") return "success";
  if (lower === "negative" || lower === "not_interested") return "error";
  if (lower === "maybe" || lower === "follow_up") return "warning";
  return "default";
}
